/**
 * Plain-text summary extractor for a tiptap (ProseMirror) document.
 *
 * Used to derive the OG/meta `description` and JSON-LD body snippet for survey
 * detail pages (AC-5.2). Lives in `shared` so the SSR server and the client
 * produce IDENTICAL summaries (no hydration drift) and the API could reuse it.
 *
 * The body is opaque (`unknown`) at the contract boundary, so we guard
 * the shape and walk text nodes defensively. Output is collapsed whitespace,
 * trimmed, and truncated to `maxLength` characters with an ellipsis.
 */
const BLOCK_TYPES = new Set([
    'paragraph',
    'heading',
    'blockquote',
    'listItem',
    'codeBlock',
]);
/** Recursion-depth cap: defends the walk against pathological / DoS bodies. */
const MAX_DEPTH = 200;
function collectText(node, out, depth) {
    if (depth > MAX_DEPTH)
        return;
    if (typeof node !== 'object' || node === null)
        return;
    const n = node;
    if (typeof n.text === 'string') {
        out.push(n.text);
    }
    if (Array.isArray(n.content)) {
        for (const child of n.content) {
            collectText(child, out, depth + 1);
        }
        // Separate block-level nodes with a space so words don't run together.
        if (n.type && BLOCK_TYPES.has(n.type)) {
            out.push(' ');
        }
    }
}
/**
 * Extract a plain-text summary from a tiptap document.
 *
 * @param body       tiptap JSON doc (opaque) — non-doc input yields "".
 * @param maxLength  max characters before truncation (default 160, OG-friendly).
 */
export function extractSummary(body, maxLength = 160) {
    if (typeof body !== 'object' || body === null)
        return '';
    const doc = body;
    if (doc.type !== 'doc')
        return '';
    const parts = [];
    collectText(doc, parts, 0);
    const text = parts.join('').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLength)
        return text;
    return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}
//# sourceMappingURL=summary.js.map